import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';

import { newsletterService } from '../../services/newsletterService';

type Props = {
  source?: string;
  headline?: string;
  subhead?: string;
  accent?: 'blue' | 'amber';
  /** 'plain' renders unstyled semantic markup (same fields and submit handler) for pages that bring their own design system, e.g. the landing page. */
  variant?: 'card' | 'plain';
};

export const NewsletterCapture: React.FC<Props> = ({
  source = 'landing_page',
  headline = 'Get the Sales Tax Newsletter',
  subhead = 'Weekly state-by-state updates, in one fast email. No login required.',
  accent = 'blue',
  variant = 'card',
}) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const accentColor = accent === 'amber' ? '#f59e0b' : '#2563eb';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!email) {
      setError('Please add an email address.');
      return;
    }

    setLoading(true);
    try {
      const response = await newsletterService.subscribe({
        email,
        name,
        company,
        source,
        send_now: false,
      });
      const message = response.data?.message || 'You are on the list!';
      setSuccess(message);
      setEmail('');
      setName('');
      setCompany('');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Could not save your email right now.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'plain') {
    return (
      <form className="nl-form" onSubmit={handleSubmit}>
        <div className="nl-row">
          <label className="field">
            <span className="field-label">Name (optional)</span>
            <input type="text" name="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Company (optional)</span>
            <input type="text" name="company" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">Email</span>
          <input type="email" name="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <button type="submit" className="key key-quiet" disabled={loading} aria-busy={loading}>
          {loading ? 'Sending' : 'Send me updates'}
        </button>
        <p className="nl-status" role="status" aria-live="polite">
          {success && <span className="nl-ok">{success}</span>}
          {error && <span className="nl-err">{error}</span>}
        </p>
        <p className="nl-note">One sales tax email a week. Confirm by email; unsubscribe in one click.</p>
      </form>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={(theme) => ({
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        bgcolor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[2],
      })}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="center">
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" sx={{ letterSpacing: 1.2, color: 'text.secondary' }}>
            Sales Tax Intel
          </Typography>
          <Typography variant="h5" fontWeight={800} sx={{ mt: 0.5 }}>
            {headline}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
            {subhead}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <ChipPill icon={<MailOutlineIcon fontSize="small" />} label="Authoritative state sources" />
            <ChipPill icon={<RocketLaunchIcon fontSize="small" />} label="Actionable next steps" />
          </Stack>
        </Box>
        <Box component="form" onSubmit={handleSubmit} sx={{ flex: 1, width: '100%' }}>
          <Stack spacing={1.5}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="filled"
                InputProps={{
                  sx: {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: '#fff',
                  },
                }}
              />
              <TextField
                label="Company (optional)"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                fullWidth
                variant="filled"
                InputProps={{
                  sx: {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: '#fff',
                  },
                }}
              />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                type="email"
                variant="filled"
                InputProps={{
                  sx: {
                    bgcolor: 'action.hover',
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={loading}
                sx={{
                  minWidth: 180,
                  bgcolor: accentColor,
                  color: '#0b1220',
                  fontWeight: 800,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#1d4ed8', color: '#fff' },
                }}
              >
                {loading ? 'Sending...' : 'Send me updates'}
              </Button>
            </Stack>
            {success && <Alert severity="success">{success}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              One sales tax email a week. Confirm by email; unsubscribe in one click.
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
};

const ChipPill = ({ icon, label }: { icon: React.ReactNode; label: string }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: 1.2,
      py: 0.6,
      borderRadius: 999,
      backgroundColor: 'action.hover',
      color: 'text.secondary',
      fontSize: 13,
      gap: 0.6,
    }}
  >
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
    <span>{label}</span>
  </Box>
);

export default NewsletterCapture;
